import { Component, inject, Inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatError, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ModifyEmailDialogModel,
  ModifyEmailRequest,
} from '../../../interfaces/dialogs/modify-email-dialog.model';
import {
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { API_PARAMS } from '../../../constants/api.constants';
import { RegisterVerifyContactService } from '../../../services/register-verify-contact.service';
import { SavePhoneRequest } from '../../../interfaces/dialogs/save-phone-dialog.model';
import { ModifyEmailDialogService } from '../../../services/modify-email-dialog.service';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { SharedStorageService } from '../../../services/shared-storage.service';

/**
 * 會員中心修改電子郵件dialog
 */
@Component({
  selector: 'app-modify-email-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    MatInput,
    MatButton,
    MatError,
    ReactiveFormsModule,
    NgClass,
    NgOptimizedImage,
  ],
  templateUrl: './modify-email-dialog.component.html',
  styleUrl: './modify-email-dialog.component.scss',
})
export class ModifyEmailDialogComponent {
  translateService: TranslateService = inject(TranslateService);
  registerVerifyContactService: RegisterVerifyContactService = inject(
    RegisterVerifyContactService
  );
  modifyEmailDialogService: ModifyEmailDialogService = inject(
    ModifyEmailDialogService
  );
  toastr: ToastrService = inject(ToastrService);

  emailControl = new FormControl('', [
    Validators.required,
    (control: FormControl) => this.emailValidator(control),
  ]); // 信箱
  otpControl = new FormControl('', [
    Validators.required,
    (control: FormControl) => this.otpValidator(control),
  ]); // otp驗證碼

  showEmailError: boolean = false; // 是否顯示信箱錯誤訊息
  otpRemainSeconds: number = 30; // otp驗證碼時間
  isSendOtpAvailable: boolean = true; // 是否可以發送otp
  showOtpError: boolean = false; // 是否顯示otp錯誤訊息
  isOtpReachedLimit: boolean = false; // 是否達到otp上限

  constructor(
    public dialogRef: MatDialogRef<ModifyEmailDialogComponent>,
    private sharedStorageService: SharedStorageService,
    @Inject(MAT_DIALOG_DATA) public data: ModifyEmailDialogModel
  ) {}

  // 信箱驗證器
  emailValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return {
        invalidFormat: this.translateService.instant(
          'error.email-error.format'
        ),
      };
    }
    
    // 檢查是否與現有email相同
    if (value === this.data.email) {
      return {
        alreadyRegister: this.translateService.instant(
          'error.email-error.already-register'
        ),
      };
    }
    
    return null;
  }

  // otp驗證碼驗證器
  otpValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    // 驗證只能輸入6位數字
    if (value && !/^\d{6}$/.test(value)) {
      return {
        invalidFormat: this.translateService.instant('error.otp-error.format'),
      };
    }
    return null;
  }

  // 獲取信箱錯誤訊息
  getEmailErrorMessage() {
    const control = this.emailControl;
    if (!control || !control.errors || !this.showEmailError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.email-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    if (errors['alreadyRegister']) {
      return errors['alreadyRegister'];
    }
    return '';
  }

  // 獲取otp錯誤訊息
  getOtpErrorMessage() {
    const control = this.otpControl;
    if (!control || !control.errors || !this.showOtpError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.otp-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    return '';
  }

  // 發送otp驗證碼
  sendOtp() {
    this.showEmailError = true;
    if (this.emailControl.valid) {
      this.otpCountDown();
      this.registerVerifyContactService
        .registerSendOtp(
          this.data.idNo,
          API_PARAMS.EMAIL,
          this.emailControl.value ?? ''
        )
        .subscribe({
          next: (res) => {
            if (res.errorMessage === '發送驗證碼次數達上限，請稍後再試') {
              this.showOtpError = false;
              this.isOtpReachedLimit = true;
            }
          },
        });
    }
  }

  // otp倒數
  otpCountDown() {
    this.isSendOtpAvailable = false;
    const interval = setInterval(() => {
      this.otpRemainSeconds--;
      if (this.otpRemainSeconds == 0) {
        clearInterval(interval);
        this.otpRemainSeconds = 30;
        this.isSendOtpAvailable = true;
      }
    }, 1000);
  }

  // 修改信箱
  modifyEmail() {
    this.showOtpError = true;
    if (this.otpControl.valid) {
      const request: ModifyEmailRequest = {
        idNo: this.data.idNo,
        chartNo: this.data.chartNo,
        email: this.emailControl.value ?? '',
        otp: this.otpControl.value ?? '',
      };

      this.modifyEmailDialogService.modifyEmail(request).subscribe({
          next: (res) => {
            if (res.statusCode === 'S0000') {
            this.toastr.success(
              this.translateService.instant('modify-email-dialog.modify-success')
            );
            this.dialogRef.close(res.data?.email);
            } else {
              this.toastr.error(
                this.translateService.instant('modify-email-dialog.modify-error')
              );
              this.dialogRef.close();
            }
          },
          
          error: (error) => {
            console.error('修改信箱失敗:', error);
            this.toastr.error(
              this.translateService.instant('modify-email-dialog.modify-error')
            );
            this.dialogRef.close();
          }
        }
      );
    }
  }

  // 關閉dialog
  closeDialog() {
    this.dialogRef.close();
  }
}
